import React from 'react'

import { AsyncButton } from 'components/Button'
import { Title, Subtitle } from 'components/IndexLayout'
import Spinner from 'components/Spinner'
import Pluralize from 'components/Pluralize'

import { PlanPicker } from '../../components'
import { BillingPlanFragment, useDraftBillingSummaryQuery } from 'gqlTypes'

import { formatPriceShort } from 'utils/money'

import {
  EstimatedPriceText,
  PlanPickerWrapper,
  Wrapper,
  ErrorMessage,
} from './WorkspaceSubscribePlan.styles'

const PLAN_PERIODICITY = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  TRIAL: '' /* This Plan shouldn't exist on FE */,
}

export interface WorkspaceSubscribePlanProps {
  selectedPlan?: BillingPlanFragment
  plans: BillingPlanFragment[]
  onPlanChange: (plan: BillingPlanFragment) => void
  numberEditors: number
  onPaymentDetails: () => Promise<void>
}

const formatPrice = (price: number) =>
  formatPriceShort(price, { trimDecimalIfZero: false })

export const WorkspaceSubscribePlan: React.FC<WorkspaceSubscribePlanProps> = props => {
  const {
    selectedPlan,
    plans,
    onPlanChange,
    onPaymentDetails,
    numberEditors,
  } = props

  const { data, loading } = useDraftBillingSummaryQuery({
    variables: {
      planId: selectedPlan?.id || '',
      seats: numberEditors,
    },
    skip: !selectedPlan,
  })

  const editorCost = data
    ? `${data.draftBillingSummary.billingCycleAmount}` // Type casting to a string to avoid 0 being falsy
    : undefined

  const planPeriodicity = selectedPlan?.type
    ? PLAN_PERIODICITY[selectedPlan.type]
    : ''

  const isDisabled = !selectedPlan || editorCost === '0'

  return (
    <>
      <Wrapper>
        <Title>Choose a Plan</Title>
        <Subtitle>You only pay for users who edit Sketch documents.</Subtitle>

        <PlanPickerWrapper>
          <PlanPicker
            plans={plans}
            onChange={onPlanChange}
            selectedPlan={selectedPlan}
            isDisabled={editorCost === '0'}
          />
        </PlanPickerWrapper>

        <EstimatedPriceText>
          {!loading ? (
            editorCost !== '0' ? (
              <p>
                Based on your <b>{numberEditors} Editor</b>{' '}
                <Pluralize
                  singular="Seat"
                  plural="Seats"
                  count={numberEditors}
                  wrapperElement={React.Fragment}
                />
                , your projected total {planPeriodicity} bill will be{' '}
                <b>{formatPrice(Number(editorCost))}</b> plus tax (if
                applicable).
              </p>
            ) : (
              <ErrorMessage>
                Something went wrong while loading your details. Please contact
                Sketch Customer Support.
              </ErrorMessage>
            )
          ) : (
            <Spinner.Flex primary />
          )}
        </EstimatedPriceText>
      </Wrapper>

      <AsyncButton
        variant="primary"
        fill
        onClick={onPaymentDetails}
        disabled={isDisabled}>
        <>Payment Details</>
      </AsyncButton>
    </>
  )
}
